// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FadingTooltip } from '@cosmotech/ui';
import { sanitizeModuleName } from '../utils/modules';
import { AddModuleDialog } from './AddModuleDialog';

export const ModuleSelector = ({
  modules,
  onModuleCreated,
  onModuleSelectionChange,
  onModuleDeleted,
  readOnly = false,
}) => {
  const { t } = useTranslation();

  const [selectedModules, setSelectedModules] = useState([]);
  const [isNewModuleDialogOpen, setIsNewModuleDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const handleModuleChange = useCallback(
    (newSelectedModules) => {
      setSelectedModules(newSelectedModules);
      onModuleSelectionChange(newSelectedModules);
    },
    [onModuleSelectionChange, setSelectedModules]
  );

  const openNewModuleDialog = useCallback(() => setIsNewModuleDialogOpen(true), []);
  const closeNewModuleDialog = useCallback(() => setIsNewModuleDialogOpen(false), []);
  const handleAddModule = useCallback(
    (newModuleName) => {
      const sanitizedModuleName = sanitizeModuleName(newModuleName);
      if (sanitizedModuleName) {
        onModuleCreated(sanitizedModuleName);
        setIsNewModuleDialogOpen(false);
        handleModuleChange([...selectedModules, sanitizedModuleName]);
      }
    },
    [handleModuleChange, selectedModules, onModuleCreated]
  );

  const handleDeleteClick = (moduleValue) => {
    setDeleteConfirmation(moduleValue);
  };

  const confirmDelete = () => {
    if (selectedModules.includes(deleteConfirmation))
      handleModuleChange(selectedModules.filter((module) => module !== deleteConfirmation));

    onModuleDeleted && onModuleDeleted(deleteConfirmation);
    setDeleteConfirmation(null);
  };

  const cancelDelete = () => {
    setDeleteConfirmation(null);
  };

  const manageModulesButton = useMemo(() => {
    if (readOnly) return null;

    const tooltipText = isEditMode
      ? t('customcomponents.interactiveMap.toolbar.buttons.stopModulesEdition', 'Stop edition')
      : t('customcomponents.interactiveMap.toolbar.buttons.startModulesEdition', 'Manage modules');
    return (
      <FadingTooltip key="manage-modules" title={tooltipText} disableInteractive={true}>
        <IconButton
          color={isEditMode ? 'secondary' : 'primary'}
          onClick={() => setIsEditMode(!isEditMode)}
          sx={{ ml: 2 }}
          data-cy="manage-modules-button"
        >
          <SettingsIcon />
        </IconButton>
      </FadingTooltip>
    );
  }, [isEditMode, t, readOnly]);

  const addModuleButton = useMemo(() => {
    if (!isEditMode) return null;

    const tooltipText = t('customcomponents.interactiveMap.toolbar.buttons.addModule', 'Add a new module');
    return (
      <FadingTooltip key="add-module" title={tooltipText} disableInteractive={true}>
        <IconButton color="primary" onClick={openNewModuleDialog} sx={{ mr: 2 }} data-cy="add-module-button">
          <AddIcon />
        </IconButton>
      </FadingTooltip>
    );
  }, [isEditMode, openNewModuleDialog, t]);

  const clearSelectionButton = useMemo(() => {
    if (selectedModules.length === 0 || readOnly) return null;
    const tooltipText = t('customcomponents.interactiveMap.toolbar.buttons.clearSelection', 'Clear selection');
    return (
      <FadingTooltip key="reset-modules" title={tooltipText} disableInteractive={true} style={{ marginLeft: 0 }}>
        <IconButton
          size="small"
          color="text.secondary"
          sx={{ opacity: 0.8, ml: 0 }}
          onClick={(event) => handleModuleChange([])}
          data-cy="reset-modules-button"
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </FadingTooltip>
    );
  }, [handleModuleChange, selectedModules, t, readOnly]);

  const toggleButtonGroupChildren = useMemo(() => {
    const children = [];
    modules.forEach((module) => {
      children.push(
        <ToggleButton
          key={module}
          value={module}
          data-cy={`module-toggle-${module}`}
          aria-label={module}
          color="primary"
          sx={{ textTransform: 'none', padding: '5px 15px' }}
        >
          {module}
        </ToggleButton>
      );
      if (isEditMode)
        children.push(
          <IconButton
            key={`delete-module-${module}`}
            size="small"
            onClick={() => handleDeleteClick(module)}
            sx={{
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'rgba(255,0,0,0.1)',
              },
              position: 'relative',
              padding: '5px 15px',
              right: 6,
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
    });
    return children;
  }, [isEditMode, modules]);

  return (
    <Stack direction="row" spacing={2} sx={{ margin: 2, alignItems: 'center' }} data-cy="module-selector">
      {manageModulesButton}
      <ToggleButtonGroup
        value={selectedModules}
        onChange={(event, value) => handleModuleChange(value)}
        aria-label="Sélection des modules"
        multiple
        exclusive={false}
        sx={{ flexWrap: 'wrap' }}
        data-cy="module-toggle-group"
      >
        {toggleButtonGroupChildren}
      </ToggleButtonGroup>

      {clearSelectionButton}
      {addModuleButton}
      <AddModuleDialog
        modules={modules}
        open={isNewModuleDialogOpen}
        onClose={closeNewModuleDialog}
        onAddModule={handleAddModule}
      />

      <Dialog
        container={() => document.getElementById('map-and-panels-container')}
        open={Boolean(deleteConfirmation)}
        onClose={cancelDelete}
        data-cy="delete-confirmation-dialog"
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le module &quot;
            {deleteConfirmation}
            &quot; ? Tous les éléments associés seront également supprimés.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} data-cy="cancel-delete-button">
            Annuler
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" data-cy="confirm-delete-button">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

ModuleSelector.propTypes = {
  modules: PropTypes.arrayOf(PropTypes.string).isRequired,
  onModuleCreated: PropTypes.func.isRequired,
  onModuleSelectionChange: PropTypes.func.isRequired,
  onModuleDeleted: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

export default ModuleSelector;
